/* global BigInt */
import { db } from "./db";
import axios from "axios";
import JSONbig from 'json-bigint';
import electronBridge, { isElectronEnv } from "./utils/electronBridge";
import Swal from 'sweetalert2';

// Polyfill BigInt.prototype.toJSON if not already defined
if (typeof BigInt !== 'undefined' && !BigInt.prototype.toJSON) {
    BigInt.prototype.toJSON = function() {
        return this.toString();
    };
}

// Cache for API preference to avoid repeated DB queries
let apiPreferenceCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Clear the API preference cache (useful when settings change)
 */
export const clearApiPreferenceCache = () => {
    apiPreferenceCache = null;
    cacheTimestamp = null;
    // Also clear sessionStorage
    if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.removeItem('use_dev_api_cache');
    }
};

/**
 * Get the useDevApi with caching
 * @returns {Promise<*>}
 */
export const getUseDepApi = async () => {
    // Check sessionStorage first (persists across page refreshes in same tab)
    if (typeof window !== 'undefined' && window.sessionStorage) {
        const cachedValue = window.sessionStorage.getItem('use_dev_api_cache');
        if (cachedValue !== null) {
            return cachedValue === 'true';
        }
    }

    // Check in-memory cache
    if (apiPreferenceCache !== null && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
        return apiPreferenceCache;
    }

    try {
        const setting = await db.settings
            .where("key")
            .equals("use_dev_api")
            .first();

        let value = false;
        
        // If setting exists and has a value property, use it
        if (setting && setting.hasOwnProperty('value')) {
            value = setting.value;
        } else {
            // Setting doesn't exist, add default value
            await db.settings.put({ key: 'use_dev_api', value: false });
        }

        // Update caches
        apiPreferenceCache = value;
        cacheTimestamp = Date.now();
        if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.setItem('use_dev_api_cache', value.toString());
        }

        return value;
    } catch (error) {
        console.error("Error accessing use_dev_api setting:", error);
        // Return default value in case of error
        return false;
    }
}

/**
 * Get the authorization token for API requests
 * Checks sessionStorage for apiKey first, falls back to environment token
 * @returns {string} The authorization token to use
 */
/**
 * Check if the stored API key has expired
 * @returns {boolean} true if expired or no expiry found
 */
export const isApiKeyExpired = () => {
    if (typeof window === 'undefined') return true;
    
    // Check both storages for expiry
    const sessionExpiry = window.sessionStorage.getItem('apiKeyExpiry');
    const localExpiry = window.localStorage.getItem('apiKeyExpiry');
    const expiryString = sessionExpiry || localExpiry;
    
    if (!expiryString) return true;
    
    // Parse the expiry date (format: "2025-09-07 11:37:09")
    const expiryDate = new Date(expiryString.replace(' ', 'T') + 'Z'); // Assume UTC
    const now = new Date();
    
    return now >= expiryDate;
};

/**
 * Clear expired API keys from both storages
 */
const clearExpiredApiKeys = () => {
    if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem('apiKey');
        window.sessionStorage.removeItem('apiKeyExpiry');
        window.localStorage.removeItem('apiKey');
        window.localStorage.removeItem('apiKeyExpiry');
    }
};

export const getAuthorizationToken = () => {
    if (typeof window === 'undefined') {
        return process.env.REACT_APP_API_TOKEN;
    }
    
    // Check if the API key has expired
    if (isApiKeyExpired()) {
        clearExpiredApiKeys();
        return process.env.REACT_APP_API_TOKEN;
    }
    
    // Check session storage first (higher priority)
    const sessionApiKey = window.sessionStorage.getItem('apiKey');
    if (sessionApiKey && sessionApiKey !== '') {
        return sessionApiKey;
    }
    
    // Check local storage as fallback (for persistent login)
    const localApiKey = window.localStorage.getItem('apiKey');
    if (localApiKey && localApiKey !== '') {
        // Move to session storage for this session
        const localExpiry = window.localStorage.getItem('apiKeyExpiry');
        if (localExpiry) {
            window.sessionStorage.setItem('apiKey', localApiKey);
            window.sessionStorage.setItem('apiKeyExpiry', localExpiry);
        }
        return localApiKey;
    }
    
    // Fall back to environment token for non-authenticated routes
    return process.env.REACT_APP_API_TOKEN;
};

/**
 * Get the public/default API token for login routes only
 * @returns {string} The default API token
 */
export const getPublicAuthToken = () => {
    return process.env.REACT_APP_API_TOKEN;
};

/**
 * Check if an error is from the API key account block or Promptus API being disabled.
 * Used in catch blocks to silently return instead of logging noisy errors.
 */
const isApiKeyBlocked = (error) => error?.message === 'API_KEY_ACCOUNT_BLOCKED' || error?.message === 'PROMPTUS_API_DISABLED';

/**
 * Get the required user API key - throws error if not available (no fallback)
 * Use this for all authenticated routes
 * @returns {string} The user's API key
 * @throws {Error} If no valid API key is available
 */
export const getRequiredAuthToken = () => {
    if (typeof window === 'undefined') {
        throw new Error('AUTH_REQUIRED');
    }

    // Block cloud API calls when Promptus API is explicitly disabled by the user
    if (window.sessionStorage.getItem('promptusApiDisabled') === 'true') {
        throw new Error('PROMPTUS_API_DISABLED');
    }

    // For API key accounts, return the stored API key instead of blocking
    // This allows local server calls (bytedance, cosy, gpu, cpu) to work
    if (window.sessionStorage.getItem('isApiKeyAccount') === 'true') {
        const apiKey = window.sessionStorage.getItem('apiKey') || window.localStorage.getItem('apiKey');
        if (apiKey && apiKey !== '') {
            return apiKey;
        }
        throw new Error('NO_API_KEY');
    }

    // Check if the API key has expired
    if (isApiKeyExpired()) {
        clearExpiredApiKeys();
        throw new Error('API_KEY_EXPIRED');
    }

    // Check session storage first (higher priority)
    const sessionApiKey = window.sessionStorage.getItem('apiKey');
    if (sessionApiKey && sessionApiKey !== '') {
        return sessionApiKey;
    }

    // Check local storage as fallback (for persistent login)
    const localApiKey = window.localStorage.getItem('apiKey');
    if (localApiKey && localApiKey !== '') {
        // Move to session storage for this session
        const localExpiry = window.localStorage.getItem('apiKeyExpiry');
        if (localExpiry) {
            window.sessionStorage.setItem('apiKey', localApiKey);
            window.sessionStorage.setItem('apiKeyExpiry', localExpiry);
        }
        return localApiKey;
    }

    throw new Error('NO_API_KEY');
};

let authPopupShownThisSession = typeof window !== 'undefined' && sessionStorage.getItem('authPopupShown') === 'true';
let firstInvalidCallTime = null;
const AUTH_POPUP_DELAY = 90 * 1000; // 90 seconds delay before showing popup

/**
 * Show authentication error popup (only once per session, after 90 second delay)
 * @param {string} errorType - The type of auth error (API_KEY_EXPIRED, NO_API_KEY, AUTH_REQUIRED)
 */
export const showAuthErrorPopup = (errorType) => {
    if (authPopupShownThisSession) return;

    // Only show popup for expired sessions, not for users who were never logged in.
    // If there's no token in localStorage, the user never logged in — don't nag them.
    const hadSession = !!window.localStorage.getItem('apiKey') ||
                       !!window.localStorage.getItem('account') ||
                       !!window.sessionStorage.getItem('apiKey');
    if (!hadSession) return;

    const currentHash = window.location.hash?.toLowerCase() || '';
    if (currentHash.includes('login') || currentHash.includes('register') || currentHash.includes('signin') || currentHash.includes('profile')) {
        return;
    }

    const now = Date.now();

    if (firstInvalidCallTime === null) {
        firstInvalidCallTime = now;
        return;
    }

    if (now - firstInvalidCallTime < AUTH_POPUP_DELAY) {
        return;
    }

    authPopupShownThisSession = true;

    // Also persist to sessionStorage so SPA navigation can't reset the flag
    sessionStorage.setItem('authPopupShown', 'true');

    Swal.fire({
        icon: 'warning',
        title: 'Session Expired',
        text: 'Your session has expired. Please log in again.',
        confirmButtonColor: '#FFB02E',
        confirmButtonText: 'OK'
    });
};

/**
 * Helper function to safely get auth token with popup on failure
 * @returns {string|null} The auth token or null if failed (popup shown)
 */
export const getAuthTokenOrShowError = () => {
    try {
        return getRequiredAuthToken();
    } catch (error) {
        showAuthErrorPopup(error.message);
        return null;
    }
};

/**
 * Check if user has a valid stored API key
 * @returns {boolean} true if user has a valid non-expired API key
 */
export const hasValidApiKey = () => {
    if (typeof window === 'undefined') return false;
    
    // Check if key exists and is not expired
    const hasSessionKey = !!window.sessionStorage.getItem('apiKey');
    const hasLocalKey = !!window.localStorage.getItem('apiKey');
    
    if (!hasSessionKey && !hasLocalKey) return false;
    
    return !isApiKeyExpired();
};

/**
 * Get the remaining time until API key expiry in milliseconds
 * @returns {number} milliseconds until expiry, or 0 if expired/not found
 */
export const getApiKeyTimeRemaining = () => {
    if (typeof window === 'undefined') return 0;
    
    const sessionExpiry = window.sessionStorage.getItem('apiKeyExpiry');
    const localExpiry = window.localStorage.getItem('apiKeyExpiry');
    const expiryString = sessionExpiry || localExpiry;
    
    if (!expiryString) return 0;
    
    const expiryDate = new Date(expiryString.replace(' ', 'T') + 'Z');
    const now = new Date();
    const remaining = expiryDate.getTime() - now.getTime();
    
    return remaining > 0 ? remaining : 0;
};

/**
 * MOMM API Configuration Helpers
 */

/**
 * Get MOMM API configuration
 * @returns {Promise<Object>} MOMM API configuration
 */
export const getMommApiConfig = async () => {
    // Use the main session API key from sessionStorage
    const sessionApiKey = typeof window !== 'undefined' && window.sessionStorage ? 
        window.sessionStorage.getItem('apiKey') : null;
    
    // Extract account details from account object in localStorage
    let accountNo = 'ACC123';
    let verificationToken = null;
    let emailHash = null;
    try {
        const account = JSON.parse(localStorage.getItem('account') || '{}');
        accountNo = account.accountNo || 'ACC123';
        verificationToken = account.verificationToken || null;
        emailHash = account.emailHash || null;
    } catch {
        accountNo = 'ACC123';
        verificationToken = null;
        emailHash = null;
    }
    
    // Use dynamic API base URL
    const apiBase = await getApiBaseUrl();
    const apiUrl = `${apiBase}/api/momm`;
    
    // Debug logging
    console.log('getMommApiConfig called:', {
        sessionApiKey: sessionApiKey ? `${sessionApiKey.substring(0, 10)}...` : null,
        accountNo,
        hasSessionStorage: typeof window !== 'undefined' && !!window.sessionStorage,
        isConfigured: !!sessionApiKey
    });
    
    return {
        apiKey: sessionApiKey,
        sessionKey: sessionApiKey,
        accountNo,
        verificationToken,
        emailHash,
        apiUrl,
        isConfigured: !!sessionApiKey
    };
};

/**
 * Set MOMM API configuration
 * @param {Object} config - Configuration object
 */
export const setMommApiConfig = (config) => {
    // Currently no MOMM-specific configuration to store
    // The session API key is managed through the main login flow
};

/**
 * Clear MOMM API configuration
 */
export const clearMommApiConfig = () => {
    // Currently no MOMM-specific configuration to clear
    // The session API key is managed through the main login flow
};

/**
 * Get the API base URL based on useDevApi setting
 * @returns {Promise<string>} The API base URL to use
 */
export const getApiBaseUrl = async () => {
    const useDevApi = await getUseDepApi();
    return useDevApi ? 'https://api.dev.promptus.ai' : 'https://api.promptus.ai';
};

/**
 * Get the correct image base URL based on dev mode setting
 * @returns {Promise<string>} The image base URL
 */
export const getImageBaseUrl = async () => {
    const useDevApi = await getUseDepApi();
    return useDevApi ? 'https://api.dev.promptus.ai' : 'https://images.promptus.ai';
};

/**
 * Check if MOMM is in online mode
 * @returns {boolean} True if online mode is enabled
 */
export const isMommOnlineMode = async () => {
    try {
        const setting = await db.settings
            .where("key")
            .equals("momm_online_mode")
            .first();
        
        return setting ? setting.value : false;
    } catch (error) {
        console.error("Error checking MOMM online mode:", error);
        return false;
    }
};

/**
 * Set MOMM online/offline mode
 * @param {boolean} isOnline - True for online mode, false for offline
 */
export const setMommOnlineMode = async (isOnline) => {
    try {
        await db.settings.put({ 
            key: 'momm_online_mode', 
            value: isOnline 
        });
    } catch (error) {
        console.error("Error setting MOMM online mode:", error);
    }
};

/**
 * Handle HTTP errors, specifically for session expiry (401)
 * @param {Error} error - The error object from the API call
 * @param {Object} dispatch - Optional dispatch object for context updates
 * @param {boolean} isUserAction - If true, bypass popup cooldown (for user-initiated actions like Generate button)
 * @returns {Object} Formatted error response
 */
export const handleHttpErrors = (error, dispatch = null, isUserAction = false) => {
    // Handle custom auth errors from getRequiredAuthToken()
    if (error.message === 'API_KEY_EXPIRED' || error.message === 'NO_API_KEY' || error.message === 'AUTH_REQUIRED') {
        showAuthErrorPopup(error.message, isUserAction);
        return {
            error: true,
            message: error.message === 'API_KEY_EXPIRED'
                ? 'Your session has expired. Please log in again.'
                : 'You must be logged in to perform this action.',
            code: 401,
            authError: true
        };
    }

    if (error.response) {
        // Check for 401 Unauthorized (session expired)
        if (error.response.status === 401) {
            // Clear session and local storage data
            if (typeof window !== 'undefined') {
                window.sessionStorage.removeItem('apiKey');
                window.sessionStorage.removeItem('apiKeyExpiry');
                window.localStorage.removeItem('apiKey');
                window.localStorage.removeItem('apiKeyExpiry');
            }

            showAuthErrorPopup('API_KEY_EXPIRED', isUserAction);

            return {
                error: true,
                message: 'Your session has expired. Please log in again.',
                code: 401,
                authError: true
            };
        } else if (error.response.status === 403) {
            return {
                error: true,
                message: 'Access denied. Please check your permissions.',
                code: 403
            };
        }
    }

    // Generic error handling
    console.error('API Error:', error);
    return {
        error: true,
        message: error.message || 'An unexpected error occurred',
        code: error.response?.status || 500
    };
};

/**
 * Get template categories
 * @returns {Promise<Array>} Array of template categories
 */
export const getTemplateCategories = async () => {
    try {
        const apiBase = await getApiBaseUrl();
        const token = getRequiredAuthToken();

        const response = await axios.get(`${apiBase}/api/templates/categories`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        return response.data;
    } catch (error) {
        console.error('Error fetching template categories:', error);
        handleHttpErrors(error);
        throw error;
    }
};

/**
 * Get template list
 * @returns {Promise<Array>} Array of templates
 */
export const getTemplateList = async () => {
    try {
        const apiBase = await getApiBaseUrl();
        const token = getRequiredAuthToken();

        const response = await axios.get(`${apiBase}/api/templates/list`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        return response.data;
    } catch (error) {
        console.error('Error fetching template list:', error);
        handleHttpErrors(error);
        throw error;
    }
};

/**
 * Get a specific template by ID
 * @param {string|number} templateId - The template ID
 * @returns {Promise<Object>} Template data
 */
export const getTemplate = async (templateId) => {
    try {
        const apiBase = await getApiBaseUrl();
        const token = getRequiredAuthToken();

        const response = await axios.get(`${apiBase}/api/templates/template/${templateId}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        return response.data;
    } catch (error) {
        console.error(`Error fetching template ${templateId}:`, error);
        handleHttpErrors(error);
        throw error;
    }
};

export const getAPIHost = async (apiState, apiDispatch) => {
    if (apiState) {
        if (!apiState.online) {
            let PROMPTUS_API = `http://${process.env.REACT_APP_PROMPTUS_LOCALHOST}`;
            try {
                const setting = await db.settings.where('key').equals('api_host').first();

                if (setting && setting.hasOwnProperty('value')) {
                    let apiHost = setting.value;
                    if (apiHost) {
                        PROMPTUS_API = `${apiHost}`;
                    }
                } else {
                    await db.settings.put({ key: 'api_host', value: PROMPTUS_API });
                }

                if (apiState.host !== PROMPTUS_API) {
                    apiDispatch({ type: "setHost", host: PROMPTUS_API });
                }
                apiDispatch({ type: 'setActiveGPU', server: false })
                apiDispatch({ type: 'setActiveCPU', server: false })
            } catch (error) {
                console.error("Error getting api_host setting:", error);
                if (apiState.host !== PROMPTUS_API) {
                    apiDispatch({ type: "setHost", host: PROMPTUS_API });
                }
            }
            return;
        }
        const apiUrl = await getApiBaseUrl();
        if (apiState.host !== apiUrl) {
            apiDispatch({ type: "setHost", host: apiUrl });
        }
    }
};

export const getServerStatus = async (apiState, generateStatusDispatch) => {
    return await fetch(`${apiState.host}/api/generate/is-busy`)
        .then(response => response.json())
        .then(data => {
            console.log('server busy status', data);
            generateStatusDispatch({ type: 'setServerStatus', serverStatus: data });
            return data;
        })
        .catch(error => {
            console.error("Error getting server status:", error);
            generateStatusDispatch({ type: 'error', message: 'Could not reach server' });
        });
}

export const getModels = async (apiState, generateStatusDispatch) => {
    try {
        const response = await axios.get(`${apiState.host}/api/generate/get-models`, { timeout: 60000 });
        return response.data;
    } catch (error) {
        console.error("Error getting models:", error);
        generateStatusDispatch({ type: 'error', message: 'Could not fetch models' });
    }
};

export const getLoras = async (apiState, generateStatusDispatch) => {
    if (!apiState.host.includes('localhost')) {
        console.log("Cannot fetch loras from server yet.");
        return { loras: [] };
    }
    try {
        const response = await axios.get(`${apiState.host}/api/generate/get-loras`);
        return response.data;
    } catch (error) {
        console.error("Error getting loras:", error);
        generateStatusDispatch({ type: 'error', message: 'Could not fetch loras' });
    }
};

// Configure json-bigint to use native bigint
const JSONbigNative = JSONbig({ useNativeBigInt: true });

// Create axios instance with json-bigint for proper BigInt handling
const axiosWithBigInt = axios.create({
    transformResponse: [(data) => {
        try {
            // Use json-bigint to parse response, preserving large numbers
            return JSONbigNative.parse(data);
        } catch (error) {
            // Fallback to regular parsing if json-bigint fails
            return JSON.parse(data);
        }
    }],
    transformRequest: [(data, headers) => {
        if (headers && headers['Content-Type'] === 'application/json') {
            // Use json-bigint to stringify, preserving large numbers as numbers (not strings)
            return JSONbigNative.stringify(data);
        }
        return data;
    }]
});

export const generatePrompt = async (prompt, apiState, generateStatusDispatch, serverUrl = null) => {
    // Convert seed_value from string to number if needed
    // json-bigint will handle large numbers properly
    
    // Handle seed in params.seed_value (regular mode)
    if (prompt?.prompt?.params?.seed_value !== null && prompt?.prompt?.params?.seed_value !== undefined) {
        const seedValue = prompt.prompt.params.seed_value;
        const model = prompt?.prompt?.params?.model || '';
        const isCosyModel = model.includes('(cosy)') || model.includes('(bytedance)');
        
        if (typeof seedValue === 'string' && seedValue !== '') {
            if (isCosyModel) {
                // For cosy models, convert to number/BigInt
                try {
                    // For very large numbers, use BigInt if available
                    if (seedValue.length > 15 && typeof BigInt !== 'undefined') {
                        prompt.prompt.params.seed_value = BigInt(seedValue);
                    } else {
                        // For smaller numbers or when BigInt is not available, use regular number
                        prompt.prompt.params.seed_value = parseInt(seedValue, 10);
                    }
                } catch (e) {
                    console.error('Error converting seed value:', e);
                    prompt.prompt.params.seed_value = null;
                }
            } else {
                // For non-cosy models, keep as string
                prompt.prompt.params.seed_value = seedValue;
            }
        } else if (typeof seedValue === 'number' || typeof seedValue === 'bigint') {
            if (!isCosyModel) {
                // Convert numeric seeds to string for non-cosy models
                prompt.prompt.params.seed_value = seedValue.toString();
            }
        }
    }
    
    // Handle seed at top level (ComfyUI/Cosy mode)
    if (prompt?.prompt?.seed !== null && prompt?.prompt?.seed !== undefined) {
        const seedValue = prompt.prompt.seed;
        if (typeof seedValue === 'string' && seedValue !== '') {
            try {
                // For very large numbers, use BigInt if available
                if (seedValue.length > 15 && typeof BigInt !== 'undefined') {
                    prompt.prompt.seed = BigInt(seedValue);
                } else {
                    // For smaller numbers or when BigInt is not available, use regular number
                    prompt.prompt.seed = parseInt(seedValue, 10);
                }
            } catch (e) {
                console.error('Error converting seed value:', e);
                prompt.prompt.seed = null;
            }
        }
    }
    
    if (!prompt.version) {
        prompt.version = process.env.REACT_APP_VERSION;
    }

    const isLocalServer = serverUrl && (serverUrl.includes('localhost') || serverUrl.includes('127.0.0.1'));

    // Strip version field for local servers — they don't expect it
    if (isLocalServer && prompt.version) {
        delete prompt.version;
    }
    if (!isLocalServer && (!prompt?.prompt?.cosyflow || (typeof prompt.prompt.cosyflow === 'string' && prompt.prompt.cosyflow.trim() === ''))) {
        console.error('[GENERATE] BLOCKED: cosyflow field is empty or missing in prompt data:', prompt?.prompt?.cosyflow);
        return { error: 'No model selected. Please select a model and try again.' };
    }

    try {
        const targetHost = serverUrl || apiState.host;
        console.log(`[GENERATE] Using server: ${targetHost} ${serverUrl ? '(from model lookup)' : '(from apiState)'}`);

        // For local servers, use getAuthorizationToken() to avoid PROMPTUS_API_DISABLED block
        // Local servers accept any token or ignore auth entirely
        const authToken = isLocalServer ? getAuthorizationToken() : getRequiredAuthToken();

        const response = await axiosWithBigInt.post(`${targetHost}/api/generate`, prompt, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${authToken}`
            }
        });
        return response.data;
    } catch (error) {
        const errorResult = handleHttpErrors(error, generateStatusDispatch, true);
        if (errorResult.code !== 401) {
            console.error("Error generating prompt:", error);
            generateStatusDispatch({ type: 'error', message: errorResult.message || 'An unexpected error occurred' });
        }
        return errorResult;
    }
};

export const getProgress = async (uid, cosyServer, apiState, generateStatusDispatch, serverUrl = null) => {
    if (uid) {
        try {
            const targetHost = serverUrl || apiState.host;
            console.log(`[PROGRESS] Using server: ${targetHost} ${serverUrl ? '(from model lookup)' : '(from apiState)'}`);

            let response = null
            if (!cosyServer) {
                response = await axios.get(`${targetHost}/api/generate/progress?uid=${uid}`);
            } else {
                response = await axios.get(`${targetHost}/api/generate/progress/${uid}`);
            }
            // const response = await axios.get(`${apiState.host}/api/generate/progress?uid=${uid}`);
            return response.data;
        } catch (error) {
            if (error.name === 'AbortError') return error;
            console.error("Error getting progress:", error);
            generateStatusDispatch({ type: 'error', message: 'An unexpected error occurred' });
        }
    }
};

export const getCosyProgress = async (uid, apiState, generateStatusDispatch, serverUrl = null) => {
    if (uid) {
        try {
            const targetHost = serverUrl || apiState.host;
            console.log(`[COSY PROGRESS] Using server: ${targetHost} ${serverUrl ? '(from model lookup)' : '(from apiState)'}`);

            const response = await axios.get(`${targetHost}/api/generate/progress/${uid}`);
            return response.data;
        } catch (error) {
            if (error.name === 'AbortError') return error;
            console.error("Error getting progress:", error);
            generateStatusDispatch({ type: 'error', message: 'An unexpected error occurred' });
        }
    }
};

export const getGenerateOutput = async (jobUid, outputId) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/generate/output/${jobUid}/${outputId}`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        const errorResult = handleHttpErrors(error);
        if (errorResult.code !== 401) {
            console.error("Error getting generate output:", error);
        }
        return errorResult;
    }
};

export const cancelJob = async (uid, account_no, generateStatusDispatch) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/generate/cancel`, {
            params: { account_no, uid }
        });
        return response.data;
    } catch (error) {
        console.error("Error cancelling job:", error);
        generateStatusDispatch({ type: 'error', message: 'Could not process cancel request' });
    }
}

export const cancelCosyJob = async (uid, apiState, generateStatusDispatch) => {
    try {
        const response = await axios.get(`${apiState.host}/api/generate/cancel/${uid}`);
        return response.data;
    } catch (error) {
        console.error("Error cancelling cosy job:", error);
        generateStatusDispatch({ type: 'error', message: 'Could not process cancel cosy request' });
    }
}

export const signin = async (email, password, ip_address, source) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/profile/signin`, { email, password, ip_address, source }, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${process.env.REACT_APP_API_TOKEN}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error signing in:", error);
        // Capture and return verification status if present
        if (error.response && error.response.data) {
            return {
                message: error.response.data.message || "Login failed. Please try again.",
                status: error.response.data.status || "error"
            };
        }
        return { message: "Login failed. Please try again.", status: "error" };
    }
}

/**
 * Logout from API session by calling the signout endpoint
 * @returns {Promise<Object>} Response from logout endpoint
 */
export const logoutApiSession = async () => {
    try {
        const sessionApiKey = window.sessionStorage.getItem('apiKey');
        
        // If no session key, just return success (nothing to logout)
        if (!sessionApiKey) {
            return { success: true, message: 'No active session' };
        }
        
        const apiInUse = await getApiBaseUrl();
        
        const response = await axios.post(`${apiInUse}/api/profile/signout`, {}, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${sessionApiKey}`
            }
        });
        
        // Clear both session and local storage after successful logout
        window.sessionStorage.removeItem('apiKey');
        window.sessionStorage.removeItem('apiKeyExpiry');
        window.localStorage.removeItem('apiKey');
        window.localStorage.removeItem('apiKeyExpiry');
        
        return response.data;
    } catch (error) {
        console.error("Error during API logout:", error);
        
        // Even if API call fails, clear all session data
        window.sessionStorage.removeItem('apiKey');
        window.sessionStorage.removeItem('apiKeyExpiry');
        window.localStorage.removeItem('apiKey');
        window.localStorage.removeItem('apiKeyExpiry');
        
        return { success: false, message: 'Logout error, but session cleared locally' };
    }
};

export const register = async (emailAddress, password, ip_address, user_agent) => {
    try {
        let apiInUse;
        await getUseDepApi().then(useDevApi => {
            if (useDevApi) {
                apiInUse = 'https://api.dev.promptus.ai';
            } else {
                apiInUse = 'https://api.promptus.ai';
            }
        });
        const source = electronBridge.isElectron ? 'PWA' : 'PWEB';
        const response = await axios.post(`${apiInUse}/api/profile/register`, {
            emailAddress,
            password,
            ip_address,
            user_agent,
            source
        }, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${process.env.REACT_APP_API_TOKEN}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error registering account:", error);
        // Capture and forward API response errors, including verification requirements
        if (error.response && error.response.data) {
            return {
                message: error.response.data.message || "Registration failed. Please try again.",
                status: error.response.data.status || "error"
            };
        }
        return { message: "Registration failed. Please try again.", status: "error" };
    }
};

export const accountNosignin = async (accountNo) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/profile/${accountNo}`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        if (isApiKeyBlocked(error)) return null;
        console.error("Error getting account:", error);
    }
}

export const getTokens = async (accountNo) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const apiKey = window.sessionStorage.getItem('apiKey') || window.localStorage.getItem('apiKey');
        const response = await axios.get(`${apiInUse}/api/profile/${accountNo}/credits`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${apiKey || getRequiredAuthToken()}`
            }
        });
        console.log("Tokens: ", response.data);
        return response.data;
    } catch (error) {
        if (isApiKeyBlocked(error)) return { credits: 0, blocked: true };
        const errorResult = handleHttpErrors(error);
        if (errorResult.code !== 401) {
            console.error("Error getting tokens:", error);
        }
        return errorResult;
    }
}

export const getSubscription = async (accountNo) => {
    try {
        let apiInUse;
        await getUseDepApi().then(useDevApi => {
            if (useDevApi) {
                apiInUse = 'https://api.dev.promptus.ai';
            } else {
                apiInUse = 'https://api.promptus.ai';
            }
        });
        const response = await axios.get(`${apiInUse}/api/profile/get-subscription/${accountNo}`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        console.log("Subscription Details: ", response.data);
        return response.data;
    } catch (error) {
        if (isApiKeyBlocked(error)) return { blocked: true };
        const errorResult = handleHttpErrors(error);
        if (errorResult.code !== 401) {
            console.error("Error getting subscription details:", error);
        }
        return errorResult;
    }
}

export const getAccountFromGoogle = async (email) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/profile/google-signin`, { email }, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error getting account from google:", error);
    }
}

export const updateAccount = async (blob, accountNo, token, metadata) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/images/update/${accountNo}`, {
            alias: null,
            token,
            password: null,
            verificationToken: null,
            images: blob,
            metadata
        }, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error updating account:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const deleteCollectionImage = async (accountNo, hash) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/images/delete/${accountNo}/${hash}`, {}, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error deleting collection image:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const updateUsernameAccount = async (username, accountNo) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/profile/${accountNo}/update`, {
            alias: username,
            token: null,
            password: null,
            verificationToken: null,
            images: null
        }, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error updating username account:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const getPublicGalleries = async () => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/publicGalleries`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error getting public galleries:", error);
    }
};

export const generateOutput = async (directory, image) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/output`, { directory, image }, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error generating output:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const registerHostingService = async (promptusID, emailAddress, alias) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/whmcs/register`, {
            id: promptusID,
            email: emailAddress,
            alias
        }, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error registering hosting service:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const changeImageVisibility = async (accountNo, hash, publicValue) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/images/public/${accountNo}/${hash}/${publicValue}`, {}, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error changing image visibility:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const getCollectionHashes = async (accountNo) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/images/collection/${accountNo}`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error getting collection hashes:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

/**
 * @deprecated Use getCollectionPaginated instead for better performance
 * This function fetches images one by one which is inefficient for large collections
 */
export const getCollectionImage = async (accountNo, hash) => {
    console.warn("getCollectionImage is deprecated. Use getCollectionPaginated for better performance.");
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/images/collection/${accountNo}/${hash}`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error getting collection image:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const getCollectionPaginated = async (accountNo, totalPerPage, pageNo) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/images/collection/${accountNo}/${totalPerPage}/${pageNo}`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error getting paginated collection:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const updateImageTags = async (accountNo, hash, tag) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/images/tag/${accountNo}/${hash}`, { tag }, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error updating image tags:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const nodeTestAdd = async (accountNo, testNode) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/node-test/add/${accountNo}/${testNode}`, {}, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error adding node test:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const nodeTestGet = async (accountNo) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/node-test/get/${accountNo}`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        let data = response.data;
        if ('data' in data) {
            data = data.data;
            console.log('nodeTestGet removed redundant data wrapper');
        }
        return data;
    } catch (error) {
        console.error("Error getting node test:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const nodeTestDelete = async (accountNo, testNode) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/node-test/delete/${accountNo}`, {}, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error deleting node test:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const getImageMetadata = async (accountNo, hash) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/images/metadata/${accountNo}/${hash}`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error getting image metadata:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const revealLocator = async (inscriptionID) => {
    try {
        const response = await axios.get(`https://ordinals.com/content/${inscriptionID}`);
        return response.data;
    } catch (error) {
        console.error("Error revealing locator:", error);
    }
};

export const getPromptFileData = async (accountNo, uid) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/images/prompt-file/${accountNo}/${uid}`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error getting prompt file data:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const getImageDescription = async (accountNo, hash, image) => {
    const apiInUse = await getApiBaseUrl()
    let url = `${apiInUse}/api/images/description/${accountNo}`;
    let options = {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${getRequiredAuthToken()}`
        }
    };

    if (hash !== "") {
        console.log("Requesting description via GET and hash");
        url += `/${hash}`;
        options.method = "GET";
    } else if (image) {
        console.log("Requesting description via POST and image");
        options.method = "POST";
        options.body = JSON.stringify({ image });
    } else {
        throw new Error("Either hash or image must be provided");
    }

    try {
        const response = await axios({
            url,
            method: options.method,
            headers: options.headers,
            data: options.body
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching image description:', error);
        const errorResult = handleHttpErrors(error);
        throw errorResult; // Re-throw the error to handle it further up in the call stack if necessary
    }
};

export const isLocalServerBusy = async (apiState) => {
    try {
        const response = await axios.get(`${apiState.host}/api/generate/is-busy`, {
            headers: {
                "Content-Type": "application/json"
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error checking if local server is busy:", error);
    }
};

export const getRecommendedSettings = async () => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/settings/get-recommended`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error getting recommended settings:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const shareToSocialMedia = async (accountNo, image, url) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/images/share/${url}/${accountNo}`, { image }, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error sharing to social media:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const updateOpenAIKey = async (openAIKey, accountNo) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/profile/${accountNo}/update`, {
            openAIKey,
            token: null,
            password: null,
            verificationToken: null,
            images: null,
            alias: null
        }, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error updating openAI key:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const updateGoogleAPIKey = async (googleAPIKey, accountNo) => {
    try {
        const apiInUse = await getApiBaseUrl();
        console.log(`Sending Google API key update request to ${apiInUse}/api/profile/${accountNo}/update`);
        const response = await axios.post(`${apiInUse}/api/profile/${accountNo}/update`, {
            googleAPIKey,
            token: null,
            password: null,
            verificationToken: null,
            images: null,
            alias: null,
            openAIKey: null
        }, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        console.log("Google API key update response:", response.data);
        return response.data;
    } catch (error) {
        console.error("Error updating Google API key:", error);
        if (error.response) {
            console.error("Server response data:", error.response.data);
            console.error("Server response status:", error.response.status);
        }
        const errorResult = handleHttpErrors(error);
        return errorResult.error ? errorResult : { error: true, message: "Server error updating Google API key" };
    }
};

export const setReceived = async (jobUid, outputUid) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/generate/received/${jobUid}/${outputUid}`, {}, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error setting received:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const getUndeliveredImages = async (accountNo) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/images/undelivered/${accountNo}`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        if (isApiKeyBlocked(error)) return { error: true, blocked: true };
        console.error("Error getting undelivered images:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};


export const getWhmcsData = async (accountNo) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/whmcs/details/${accountNo}`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        if (isApiKeyBlocked(error)) return null;
        console.error("Error getting whmcs data:", error);
        const errorResult = handleHttpErrors(error);
        return null; // Return null instead of undefined
    }
};

export const getCosyflows = async (apiState, generateStatusDispatch) => {
    try {
        const response = await axios.get(`${apiState.host}/api/generate/get-cosyflows`);
        return response.data;
    } catch (error) {
        console.error("Error getting cosyflows:", error);
        // generateStatusDispatch({ type: 'error', message: 'Could not fetch cosyflows' });
    }
};

export const getCosyflowData = async (cosyflow, apiState, generateStatusDispatch) => {
    try {
        const response = await axios.get(`${apiState.host}/api/generate/get-cosyflow-variables/${cosyflow}`);
        return response.data;
    } catch (error) {
        console.error("Error getting cosyflows:", error);
        generateStatusDispatch({ type: 'error', message: 'Could not fetch cosyflows' });
    }
};

export const getComfyOutput = async (uid, outputID, apiState, generateStatusDispatch, serverUrl = null) => {
    if (uid) {
        try {
            const targetHost = serverUrl || apiState.host;
            console.log(`[COMFY OUTPUT] Using server: ${targetHost} ${serverUrl ? '(from model lookup)' : '(from apiState)'}`);

            const response = await axios.get(`${targetHost}/api/generate/output/${uid}/${outputID}`);
            return response.data;
        } catch (error) {
            if (error.name === 'AbortError') return error;
            console.error("Error getting comfy output:", error);
            generateStatusDispatch({ type: 'error', message: 'An unexpected error occurred' });
        }
    }
};

// IP cache
let cachedIP = null;
let ipCacheTimestamp = null;
const IP_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

export const getGoogleAuth = async () => {
    let ip = '';
    
    // Try to get cached IP first
    if (cachedIP && ipCacheTimestamp && (Date.now() - ipCacheTimestamp < IP_CACHE_DURATION)) {
        ip = cachedIP;
    } else {
        // Fetch IP with timeout (make it optional - don't fail if IP fetch fails)
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 2000); // 2 second timeout
            
            const response = await fetch('https://api.ipify.org/?format=text', {
                signal: controller.signal
            });
            clearTimeout(timeoutId);
            
            ip = await response.text();
            console.log("IP ADDRESS ", ip);
            
            // Cache the IP
            cachedIP = ip;
            ipCacheTimestamp = Date.now();
        } catch (error) {
            console.warn("Failed to fetch IP address, proceeding without it:", error);
            ip = ''; // Continue without IP if fetch fails
        }
    }

    let source = window.location.href;
    if (electronBridge.isElectron) {
        source = 'pwa'
    }
    const action = "login";
    console.log(`Source: ${source}`);
    console.log(`Ip: ${ip}`);
    console.log(`action: ${action}`);  

    // Get API URL (now using cached version)
    const apiInUse = await getApiBaseUrl();

    try {
        // Add timeout to main API request
        const response = await axios.get(`${apiInUse}/api/signin/google-auth?s=${encodeURIComponent(source)}&ip=${encodeURIComponent(ip)}&a=${encodeURIComponent(action)}`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getPublicAuthToken()}`
            },
            timeout: 5000 // 5 second timeout
        });
        return response.data;
    } catch (error) {
        console.error("Error getting google auth data:", error);
        const errorResult = handleHttpErrors(error);
        throw errorResult; // Re-throw to be handled by caller
    }
}
export const autoLogin = async (data) => {
    console.log("CALLED autoLogin")
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/profile/auto-signin`, data, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getPublicAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error setting received:", error);
    }
};

export const resetPassword = async (emailAddress) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/profile/reset-password`, { email_address: emailAddress }, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getPublicAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error resetting password:", error);
        const errorResult = handleHttpErrors(error);
        // Return error message from API if available
        if (error.response && error.response.data) {
            return {
                message: error.response.data.message || "Password reset failed. Please try again.",
                status: error.response.data.status || "error"
            };
        }
        return errorResult.error ? errorResult : { message: "Password reset failed. Please try again.", status: "error" };
    }
}

export const getVersionFromAPI = async () => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/versions/get`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        if (isApiKeyBlocked(error)) return null;
        console.error("Error getting version:", error);
    }
}

export const getUsersMonthlyCredits = async (userAccountNo) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/profile/${userAccountNo}/credits`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        if (isApiKeyBlocked(error)) return null;
        console.error("Error getting monthly credits:", error);
    }
}

export const getSubscriptionPortal = async () => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/stripe/subscription/portal`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        console.log("[SubscriptionManage] Subscription Portal: ", response.data);
        return response.data;
    } catch (error) {
        const errorResult = handleHttpErrors(error);
        if (errorResult.code !== 401) {
            console.error("Error getting subscription portal:", error);
        }
        return errorResult;
    }
}

export const getCheckoutDetails = async (cs) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/stripe/checkout/${cs}`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        console.log("[PaymentStatus] Checkout Details: ", response.data);
        return response.data;
    } catch (error) {
        const errorResult = handleHttpErrors(error);
        if (errorResult.code !== 401) {
            console.error("Error getting checkout details:", error);
        }
        return errorResult;
    }
}

export const updateCustomCosyflow = async (payload) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/cosyflows/update`, payload, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error(`Error updating custom cosyflow:`, error);
        handleHttpErrors(error);
        throw error;
    }
};

export const enhancePromptAPI = async (prompt) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/prompt/enhance`, { prompt }, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error enhancing prompt:", error);
        const errorResult = handleHttpErrors(error, null, true);
        return errorResult;
    }
};

export const getCosyflowCategories = async () => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/cosyflows/categories`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        console.log("[CosyflowCategories] Cosyflow Categories: ", response.data);
        return response.data;
    } catch (error) {
        const errorResult = handleHttpErrors(error);
        if (errorResult.code !== 401) {
            console.error("Error getting cosyflow Categories:", error);
        }
        return errorResult;
    }
}

export const createLongLifeAPIKey = async (accountNo) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/public/create-api-key/${accountNo}`, {
            api_key_type_id: 4,
            purpose: "New worker key"
         }, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error creating long life API Key:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const getLongLifeAPIKey = async () => {
    if (typeof window === 'undefined') return null;

    const sessionKey = window.sessionStorage.getItem('longLifeAPIKey');
    if (sessionKey && !isLongLifeAPIKeyExpired()) {
        return sessionKey;
    }

    const localKey = window.localStorage.getItem('longLifeAPIKey');
    if (localKey && !isLongLifeAPIKeyExpired()) {
        window.sessionStorage.setItem('longLifeAPIKey', localKey);
        const localExpiry = window.localStorage.getItem('longLifeAPIKeyExpiry');
        if (localExpiry) {
            window.sessionStorage.setItem('longLifeAPIKeyExpiry', localExpiry);
        }
        return localKey;
    }

    try {
        const { db } = await import('./db');
        const stored = await db.account.where('key').equals('longLifeAPIKey').first();
        if (stored && stored.value && stored.value.key) {
            const expiry = stored.value.expiry;
            if (expiry) {
                const expiryDate = new Date(expiry.replace(' ', 'T') + 'Z');
                if (new Date() < expiryDate) {
                    window.sessionStorage.setItem('longLifeAPIKey', stored.value.key);
                    window.sessionStorage.setItem('longLifeAPIKeyExpiry', expiry);
                    window.localStorage.setItem('longLifeAPIKey', stored.value.key);
                    window.localStorage.setItem('longLifeAPIKeyExpiry', expiry);
                    return stored.value.key;
                }
            }
        }
    } catch (error) {
        console.error('Error retrieving long life API key from IndexedDB:', error);
    }

    return null;
};

export const setLongLifeAPIKey = async (key, expiry) => {
    if (typeof window === 'undefined' || !key) return;

    window.sessionStorage.setItem('longLifeAPIKey', key);
    window.localStorage.setItem('longLifeAPIKey', key);

    if (expiry) {
        window.sessionStorage.setItem('longLifeAPIKeyExpiry', expiry);
        window.localStorage.setItem('longLifeAPIKeyExpiry', expiry);
    }

    try {
        const { db } = await import('./db');
        await db.account.put({ key: 'longLifeAPIKey', value: { key, expiry } });
        console.log('[LongLifeAPIKey] Stored in all locations');
    } catch (error) {
        console.error('Error storing long life API key in IndexedDB:', error);
    }
};

export const clearLongLifeAPIKey = async () => {
    if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem('longLifeAPIKey');
        window.sessionStorage.removeItem('longLifeAPIKeyExpiry');
        window.localStorage.removeItem('longLifeAPIKey');
        window.localStorage.removeItem('longLifeAPIKeyExpiry');
    }

    try {
        const { db } = await import('./db');
        await db.account.where('key').equals('longLifeAPIKey').delete();
        console.log('[LongLifeAPIKey] Cleared from all locations');
    } catch (error) {
        console.error('Error clearing long life API key from IndexedDB:', error);
    }
};

export const isLongLifeAPIKeyExpired = () => {
    if (typeof window === 'undefined') return true;

    const sessionExpiry = window.sessionStorage.getItem('longLifeAPIKeyExpiry');
    const localExpiry = window.localStorage.getItem('longLifeAPIKeyExpiry');
    const expiryString = sessionExpiry || localExpiry;

    if (!expiryString) return true;

    const expiryDate = new Date(expiryString.replace(' ', 'T') + 'Z');
    return new Date() >= expiryDate;
};

export const getLongLifeAPIKeyTimeRemaining = () => {
    if (typeof window === 'undefined') return 0;

    const sessionExpiry = window.sessionStorage.getItem('longLifeAPIKeyExpiry');
    const localExpiry = window.localStorage.getItem('longLifeAPIKeyExpiry');
    const expiryString = sessionExpiry || localExpiry;

    if (!expiryString) return 0;

    const expiryDate = new Date(expiryString.replace(' ', 'T') + 'Z');
    const remaining = expiryDate.getTime() - new Date().getTime();
    return remaining > 0 ? remaining : 0;
};

export const refreshLongLifeAPIKeyIfNeeded = async () => {
    const REFRESH_THRESHOLD = 24 * 60 * 60 * 1000; // 24 hours
    const remaining = getLongLifeAPIKeyTimeRemaining();

    if (remaining > 0 && remaining < REFRESH_THRESHOLD) {
        console.log('[LongLifeAPIKey] Key expiring soon, attempting refresh...');
        const sessionApiKey = window.sessionStorage.getItem('apiKey');
        const storedAccount = window.localStorage.getItem('account');
        let accountNo = null;
        if (storedAccount) {
            try {
                const account = JSON.parse(storedAccount);
                accountNo = account.accountNo;
            } catch (e) {
                console.error('[LongLifeAPIKey] Error parsing stored account:', e);
            }
        }
        if (sessionApiKey && accountNo) {
            try {
                const response = await createLongLifeAPIKey(accountNo);
                if (response && response.api_key) {
                    await setLongLifeAPIKey(response.api_key, response.api_key_expiry);
                    console.log('[LongLifeAPIKey] Key refreshed successfully');
                    return response.api_key;
                }
            } catch (error) {
                console.error('[LongLifeAPIKey] Failed to refresh key:', error);
            }
        }
    }

    return await getLongLifeAPIKey();
};

export const getSubscriptionActive = async () => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/stripe/subscription/is-active`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        console.log("[SubscriptionManage] Subscription Active: ", response.data);
        return response.data;
    } catch (error) {
        const errorResult = handleHttpErrors(error);
        if (errorResult.code !== 401) {
            console.error("Error getting subscription status:", error);
        }
        return errorResult;
    }
}

export const deleteUserCosyflow = async (cosyflowUID , accountNo) => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.post(`${apiInUse}/api/cosyflows/delete`, {
                uid: cosyflowUID,
                account_no: accountNo
            } , {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error deleting user's cosyflow:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

export const deleteLocalCosyflow = async (serverUrl , cosyflowTitle) => {
    try {
        const response = await axios.post(`${serverUrl}/api/cosy/uninstall-local`, {
                title: cosyflowTitle
            } , {
            headers: {
                "Content-Type": "application/json",
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error deleting local cosyflow:", error);
        const errorResult = handleHttpErrors(error);
        return errorResult;
    }
};

const API_KEY_VALIDATION_ENDPOINTS = [
    'https://papi.promptus.ai',
    'http://localhost:3002'
];

export const verifyAndLoginWithApiKey = async (apiKey, email) => {
    try {
        let response = null;
        let lastError = null;

        // Try official endpoint first, fall back to localhost
        for (const endpoint of API_KEY_VALIDATION_ENDPOINTS) {
            try {
                console.log(`[API_KEY_LOGIN] Trying ${endpoint}...`);
                const url = `${endpoint}/api/payment/bytedance-key/validate/${encodeURIComponent(apiKey)}`;
                const body = email ? { email } : {};
                response = await axios.post(url, body, {
                    headers: { "Content-Type": "application/json" },
                    timeout: 10000
                });
                console.log(`[API_KEY_LOGIN] Success from ${endpoint}`);
                break;
            } catch (err) {
                lastError = err;
                // If we got a meaningful HTTP error (401/403/404), don't try next endpoint
                if (err.response?.status) {
                    console.log(`[API_KEY_LOGIN] Got HTTP ${err.response.status} from ${endpoint}`);
                    throw err;
                }
                console.log(`[API_KEY_LOGIN] ${endpoint} unreachable, trying next...`);
            }
        }

        if (!response) {
            throw lastError || new Error('All validation endpoints unreachable');
        }

        if (response.data?.valid) {
            window.sessionStorage.setItem('apiKey', apiKey);
            window.sessionStorage.setItem('apiKeyExpiry', '2099-12-31 23:59:59');
            window.sessionStorage.setItem('isApiKeyAccount', 'true');
            window.localStorage.setItem('apiKey', apiKey);
            window.localStorage.setItem('apiKeyExpiry', '2099-12-31 23:59:59');

            return {
                success: true,
                credits: 0,
                account: {
                    accountNo: response.data.claimedBy || 'api-key-user',
                    alias: response.data.claimedBy || 'API Key User',
                    accountTypeId: '0',
                    isApiKeyAccount: true
                }
            };
        }

        return { success: false, message: response.data?.message || 'Invalid API Key' };
    } catch (error) {
        window.sessionStorage.removeItem('apiKey');
        window.sessionStorage.removeItem('apiKeyExpiry');
        window.sessionStorage.removeItem('isApiKeyAccount');

        if (error.response?.status === 401 || error.response?.status === 404) {
            return { success: false, message: 'Invalid API Key' };
        }
        if (error.response?.status === 403) {
            return { success: false, message: 'Access denied for this API Key' };
        }
        console.error('[API_KEY_LOGIN] Validation error:', error);
        return { success: false, message: 'Verification failed. Please check your API Key and try again.' };
    }
};

export const getDefaultModels = async () => {
    try {
        const apiInUse = await getApiBaseUrl();
        const response = await axios.get(`${apiInUse}/api/models/get-default`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getRequiredAuthToken()}`
            }
        });
        console.log("[DefaultModels] Default Models: ", response.data);
        return response.data;
    } catch (error) {
        if (isApiKeyBlocked(error)) return [];
        const errorResult = handleHttpErrors(error);
        if (errorResult.code !== 401) {
            console.error("Error getting Default Models:", error);
        }
        return errorResult;
    }
}